
(function() {

/*
Autoplay-to-next-slide for Swiper videos

- Goal:
  When the active slide's <video> finishes playing, automatically advance to the next slide.

- How it works:
  1) On init and on slideChangeTransitionStart:
     - Pause and reset all videos in the section (pause + currentTime = 0).
     - Remove any previously attached 'ended' listeners.
  2) For the active slide:
     - If its <video> is marked for autoplay (via `autoplay` or `data-autoplay="true"`),
       force muted, disable loop (so 'ended' can fire), then call play().
     - Attach a one-time 'ended' listener that calls `swiper.slideNext()`.

- Notes:
  - Mobile browsers typically require muted + playsinline to allow autoplay.
  - Loop is disabled to ensure the 'ended' event is emitted.
*/
  class KikSwipperTab extends HTMLElement {
    connectedCallback() {
      this._inited = false;
      this._ensureInit();
    }
    disconnectedCallback() {
      this._cleanup();
    }
    _ensureInit() {
      if (this._inited) return;
      window.loadSwiper().then(() => this._init());
    }
    _init() {
      if (this._inited) return;
      var root = this;
      var sliderEl = root.querySelector('.kik-swipper__slider');
      if (!sliderEl) return;
      var prevEl = root.querySelector('.prev-btn');
      var nextEl = root.querySelector('.next-btn');
      var scrollbarEl = root.querySelector('.kik-swipper__scrollbar');
      var swiperOptions = {
        effect: 'fade',
        fadeEffect: { crossFade: true },
        loop: true,
        speed: 600,
        slidesPerView: 1,
        slidesPerGroup: 1,
        spaceBetween: 0,
        allowTouchMove: true,
        grabCursor: true,
        watchSlidesProgress: true
      };
      if (prevEl && nextEl) {
        swiperOptions.navigation = { enabled: true, prevEl: prevEl, nextEl: nextEl };
      }
      if (scrollbarEl) {
        swiperOptions.scrollbar = { el: scrollbarEl, draggable: true, hide: false, snapOnRelease: true };
      }
      window.loadSwiper().then(() => {
        this.swiper = new Swiper(sliderEl, swiperOptions);
        this._bind(root);
      });
    }
    _bind(root) {
      this.buttons = root.querySelectorAll('.kik-swipper__actions button');
      Array.prototype.forEach.call(this.buttons, (btn, i) => {
        if (!btn.getAttribute('data-slide')) btn.setAttribute('data-slide', i);
      });
      this._buttonHandlers = [];
      Array.prototype.forEach.call(this.buttons, (btn) => {
        var handler = () => {
          var idx = parseInt(btn.getAttribute('data-slide'));
          if (isNaN(idx)) idx = 0;
          if (this.swiper && typeof this.swiper.slideToLoop === 'function') {
            this.swiper.slideToLoop(idx);
          } else if (this.swiper && typeof this.swiper.slideTo === 'function') {
            this.swiper.slideTo(idx);
          }
        };
        btn.addEventListener('click', handler);
        this._buttonHandlers.push([btn, handler]);
      });
      this.captionEl = root.querySelector('#kik-swipper-tab-caption');
      var initialIndex = this._getRealIndex();
      this._syncCaption(initialIndex);
      this._syncActiveButton(initialIndex);
      this._handleVideoPlayback();
      this._onSlideChangeStart = () => { this._handleVideoPlayback(); };
      this._onSlideChange = () => {
        var idx = this._getRealIndex();
        this._syncCaption(idx);
        this._syncActiveButton(idx);
      };
      this.swiper.on('slideChangeTransitionStart', this._onSlideChangeStart);
      this.swiper.on('slideChange', this._onSlideChange);
      this._inited = true;
    }
    _getRealIndex() {
      var s = this.swiper;
      return (s && typeof s.realIndex === 'number') ? s.realIndex : (s ? s.activeIndex : 0);
    }
    _getCaptionByIndex(i) {
      var buttons = this.buttons || [];
      var btn = buttons[i];
      if (!btn) return '';
      var c = btn.getAttribute('data-caption');
      if (c && c.length) return c;
      var s = btn.querySelector('span');
      var t = s ? s.textContent : btn.textContent;
      return (t || '').trim();
    }
    _syncCaption(i) {
      if (!this.captionEl) return;
      this.captionEl.textContent = this._getCaptionByIndex(i);
    }
    _syncActiveButton(i) {
      var buttons = this.buttons || [];
      Array.prototype.forEach.call(buttons, function(btn, idx) {
        if (!btn) return;
        if (idx === i) {
          btn.classList.add('active');
        } else {
          btn.classList.remove('active');
        }
      });
    }
    _isAutoPlayVideo(video) {
      if (!video) return false;
      if (video.hasAttribute('autoplay')) return true;
      var d = video.getAttribute('data-autoplay');
      return d === 'true' || d === '';
    }
    _handleVideoPlayback() {
      if (this._autoTimer) {
        clearTimeout(this._autoTimer);
        this._autoTimer = null;
      }
      if (this._videoPollTimer) {
        clearInterval(this._videoPollTimer);
        this._videoPollTimer = null;
      }
      var allVideos = this.querySelectorAll('.kik-swipper__slider video');
      Array.prototype.forEach.call(allVideos, function(v) {
        try { v.pause(); } catch(e) {}
        try { v.currentTime = 0; } catch(e) {}
        if (v._onEndedRef) { v.removeEventListener('ended', v._onEndedRef); v._onEndedRef = null; }
      });
      var s = this.swiper; if (!s) return;
      var activeSlide = s.slides[s.activeIndex]; if (!activeSlide) return;
      var autoplayEnabled = (this.getAttribute('data-autoplay') === 'true');
      var delayAttr = this.getAttribute('data-delay');
      var autoplayDelay = parseInt(delayAttr || '5000', 10);
      // Detect local video-media wrapper (Shopify hosted video, not YouTube/Vimeo iframe)
      var localWrapper = activeSlide.querySelector('video-media:not([host])');
      // Choose the visible video on current viewport (desktop/mobile)
      var activeVideo = null;
      var vids = activeSlide.querySelectorAll('video');
      if (vids && vids.length) {
        Array.prototype.some.call(vids, function(v){
          var st = window.getComputedStyle(v);
          if (st && st.display !== 'none' && st.visibility !== 'hidden' && v.offsetWidth + v.offsetHeight > 0) {
            activeVideo = v; return true;
          }
          return false;
        });
        if (!activeVideo) { activeVideo = vids[0]; }
      }
      if (!autoplayEnabled) {
        // Auto slide disabled: just play active video (if any), never auto-advance, keep native loop behaviour.
        if (activeVideo && this._isAutoPlayVideo(activeVideo)) {
          activeVideo.muted = true;
          var p0 = activeVideo.play && activeVideo.play();
          if (p0 && typeof p0.catch === 'function') { p0.catch(function(){}); }
        } else if (!activeVideo && localWrapper) {
          // Video tag not in DOM yet (deferred-media). Poll until it appears, then just play once (loop left intact).
          var tries = 0;
          var pollNoAdvance = () => {
            if (tries++ > 20) {
              clearInterval(this._videoPollTimer);
              this._videoPollTimer = null;
              return;
            }
            var v = localWrapper.querySelector('video');
            if (!v) return;
            clearInterval(this._videoPollTimer);
            this._videoPollTimer = null;
            v.muted = true;
            var p1 = v.play && v.play();
            if (p1 && typeof p1.catch === 'function') { p1.catch(function(){}); }
          };
          this._videoPollTimer = setInterval(pollNoAdvance, 250);
        }
        return;
      }
      // Auto slide enabled
      if (activeVideo && this._isAutoPlayVideo(activeVideo)) {
        // Video slide: wait for video end to advance.
        activeVideo.muted = true;
        activeVideo.loop = false;
        if (activeVideo.hasAttribute('loop')) activeVideo.removeAttribute('loop');
        var onEnded = () => {
          if (!this.swiper) return;
          this.swiper.slideNext();
        };
        activeVideo._onEndedRef = onEnded;
        activeVideo.addEventListener('ended', onEnded, { once: true });
        var p = activeVideo.play && activeVideo.play();
        if (p && typeof p.catch === 'function') { p.catch(function(){}); }
      } else if (!activeVideo && localWrapper) {
        // Local video via <video-media> but <video> not yet in DOM (first time). Poll until it appears,
        // then behave like the branch above: play video and advance on ended. Do NOT set a time-based auto slide here.
        var tries2 = 0;
        var pollWithAdvance = () => {
          if (tries2++ > 20) {
            clearInterval(this._videoPollTimer);
            this._videoPollTimer = null;
            return;
          }
          var v = localWrapper.querySelector('video');
          if (!v) return;
          clearInterval(this._videoPollTimer);
          this._videoPollTimer = null;
          v.muted = true;
          v.loop = false;
          if (v.hasAttribute('loop')) v.removeAttribute('loop');
          var onEnded2 = () => {
            if (!this.swiper) return;
            this.swiper.slideNext();
          };
          v._onEndedRef = onEnded2;
          v.addEventListener('ended', onEnded2, { once: true });
          var p2 = v.play && v.play();
          if (p2 && typeof p2.catch === 'function') { p2.catch(function(){}); }
        };
        this._videoPollTimer = setInterval(pollWithAdvance, 250);
      } else if (autoplayDelay && autoplayDelay > 0) {
        // Image (or non-video) slide: auto-advance after delay.
        this._autoTimer = setTimeout(() => {
          if (!this.swiper) return;
          this.swiper.slideNext();
        }, Math.max(1000, autoplayDelay));
      }
    }
    _cleanup() {
      if (this._buttonHandlers) {
        this._buttonHandlers.forEach(pair => { pair[0].removeEventListener('click', pair[1]); });
        this._buttonHandlers = null;
      }
      if (this._autoTimer) {
        clearTimeout(this._autoTimer);
        this._autoTimer = null;
      }
      if (this.swiper && typeof this.swiper.off === 'function') {
        if (this._onSlideChangeStart) this.swiper.off('slideChangeTransitionStart', this._onSlideChangeStart);
        if (this._onSlideChange) this.swiper.off('slideChange', this._onSlideChange);
      }
      if (this.swiper && typeof this.swiper.destroy === 'function') {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
      var allVideos = this.querySelectorAll('.kik-swipper__slider video');
      Array.prototype.forEach.call(allVideos, function(v) {
        if (v._onEndedRef) { v.removeEventListener('ended', v._onEndedRef); v._onEndedRef = null; }
      });
      this._inited = false;
    }
  }
  if (!customElements.get('kik-swipper-tab')) {
    customElements.define('kik-swipper-tab', KikSwipperTab);
  }

  class KikStickyNav extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this._targets = [];
      this._boundClick = this._onClick.bind(this);
      this.addEventListener('click', this._boundClick);

      this._boundWindowResize = this._onWindowResize.bind(this);
      window.addEventListener('resize', this._boundWindowResize);

      this._boundScroll = this._onWindowScroll.bind(this);
      window.addEventListener('scroll', this._boundScroll, { passive: true });

      if (typeof ResizeObserver !== 'undefined') {
        this._resizeObserver = new ResizeObserver(this._setHeight.bind(this));
        this._resizeObserver.observe(this);
      }

      this._collectTargets();
      this._setHeight();
      this._updateActiveFromScroll();
    }
    disconnectedCallback() {
      if (this._boundClick) {
        this.removeEventListener('click', this._boundClick);
        this._boundClick = null;
      }
      if (this._boundWindowResize) {
        window.removeEventListener('resize', this._boundWindowResize);
        this._boundWindowResize = null;
      }
      if (this._boundScroll) {
        window.removeEventListener('scroll', this._boundScroll);
        this._boundScroll = null;
      }
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
      if (this._scrollRaf) {
        cancelAnimationFrame(this._scrollRaf);
        this._scrollRaf = null;
      }
    }
    _onClick(event) {
      var link = event.target.closest('.kik-sticky-nav__link');
      if (!link) return;
      var targetId = link.getAttribute('data-target-id') || '';
      if (!targetId) return;
      event.preventDefault();
      this._setScrollLock(link);
      this._setActive(link);
      this._scrollToTarget(targetId);
    }
    _collectTargets() {
      var links = this.querySelectorAll('.kik-sticky-nav__link');
      var result = [];
      Array.prototype.forEach.call(links, function(link) {
        if (!link) return;
        var targetId = link.getAttribute('data-target-id') || '';
        if (!targetId) return;
        var target = this._resolveTarget(targetId);
        if (target) {
          result.push({ link: link, target: target });
        }
      }.bind(this));
      this._targets = result;
      return result;
    }
    _resolveTarget(id) {
      var selectorId = id && id.charAt(0) === '#' ? id.slice(1) : id;
      return (
        document.getElementById(selectorId) ||
        document.querySelector('[data-section-id="' + selectorId + '"]')
      );
    }
    _clearActive() {
      var links = this.querySelectorAll('.kik-sticky-nav__link.active');
      Array.prototype.forEach.call(links, function(btn) {
        btn.classList.remove('active');
      });
      this._currentActive = null;
    }
    _setScrollLock(link) {
      this._scrollLockActive = true;
      this._scrollLockLink = link || null;
      if (this._scrollLockTimer) {
        clearTimeout(this._scrollLockTimer);
        this._scrollLockTimer = null;
      }
    }
    _clearScrollLock() {
      if (this._scrollLockTimer) {
        clearTimeout(this._scrollLockTimer);
        this._scrollLockTimer = null;
      }
      this._scrollLockActive = false;
      this._scrollLockLink = null;
    }
    _scheduleScrollUnlock() {
      var unlockDelay = 160;
      if (this._scrollLockTimer) {
        clearTimeout(this._scrollLockTimer);
      }
      this._scrollLockTimer = setTimeout(() => {
        this._scrollLockTimer = null;
        this._clearScrollLock();
        this._updateActiveFromScroll();
      }, unlockDelay);
    }
    _setActive(link, options) {
      if (!link) return;
      var prevActive = this._currentActive;
      this._currentActive = link;
      var links = this.querySelectorAll('.kik-sticky-nav__link');
      Array.prototype.forEach.call(links, function(btn) {
        if (btn === link) {
          btn.classList.add('active');
        } else {
          btn.classList.remove('active');
        }
      });
      if (prevActive !== link) {
        this._scrollActiveIntoView(link, options);
      }
    }
    _scrollActiveIntoView(link, options) {
      var nav = this.querySelector('nav');
      if (!nav || !link) return;
      if (nav.scrollWidth <= nav.clientWidth) return;
      var isMobile = false;
      try {
        isMobile = window.matchMedia ? window.matchMedia('(max-width: 767px)').matches : (window.innerWidth < 768);
      } catch(e) {
        isMobile = window.innerWidth < 768;
      }
      if (!isMobile && !(options && options.force)) return;
      var navRect = nav.getBoundingClientRect();
      var linkRect = link.getBoundingClientRect();
      var padding = 12;
      if (linkRect.right > navRect.right) {
        var deltaRight = linkRect.right - navRect.right + padding;
        nav.scrollTo({ left: nav.scrollLeft + deltaRight, behavior: 'smooth' });
      } else if (linkRect.left < navRect.left) {
        var deltaLeft = linkRect.left - navRect.left - padding;
        nav.scrollTo({ left: nav.scrollLeft + deltaLeft, behavior: 'smooth' });
      }
    }
    _setHeight() {
      var height = this.getBoundingClientRect().height;
      if (height > 0) {
        document.documentElement.style.setProperty('--sticky-nav-height', height.toFixed(1) + 'px');
      }
    }
    _onWindowScroll() {
      if (this._scrollRaf) return;
      this._scrollRaf = requestAnimationFrame(() => {
        this._scrollRaf = null;
        if (this._scrollLockActive) {
          if (this._scrollLockLink) {
            this._setActive(this._scrollLockLink, { fromScroll: true });
          }
          this._scheduleScrollUnlock();
          return;
        }
        this._updateActiveFromScroll();
      });
    }
    _onWindowResize() {
      this._setHeight();
      this._updateActiveFromScroll();
    }
    _scrollToTarget(id) {
      var target = this._resolveTarget(id);
      if (!target) return;
      var rect = target.getBoundingClientRect();
      var currentY = window.pageYOffset || document.documentElement.scrollTop || 0;
      var offset = this._getScrollOffset();
      var dest = rect.top + currentY - offset;
      window.scrollTo({ top: dest, behavior: 'smooth' });
    }
    _updateActiveFromScroll() {
      if (this._scrollLockActive) return;
      var targets = this._targets && this._targets.length ? this._targets : this._collectTargets();
      if (!targets || !targets.length) return;
      var offset = this._getScrollOffset();
      var scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
      var marker = scrollY + offset + 1;
      var best = null;
      var bestTop = -Infinity;
      Array.prototype.forEach.call(targets, function(entry) {
        var node = entry && entry.target;
        if (!node || typeof node.getBoundingClientRect !== 'function') return;
        var rect = node.getBoundingClientRect();
        var top = rect.top + scrollY;
        if (marker >= top && top >= bestTop) {
          bestTop = top;
          best = entry;
        }
      });
      if (best && best.link) {
        this._setActive(best.link, { fromScroll: true });
      } else {
        this._clearActive();
      }
    }
    _getScrollOffset() {
      var fallback = 0;
      var offsetAttr = this.getAttribute('data-offset');
      var parsedAttr = parseInt(offsetAttr || '0', 10);
      if (!isNaN(parsedAttr)) { fallback = parsedAttr; }
      var root = document.documentElement;
      var styles = root ? window.getComputedStyle(root) : null;
      if (!styles) return fallback;
      var parseVar = function(name) {
        var val = styles.getPropertyValue(name) || '';
        var num = parseFloat(val);
        return isNaN(num) ? 0 : num;
      };
      var header = parseVar('--sticky-header-height');
      if (header <= 0) {
        header = parseVar('--header-height');
      }
      var nav = parseVar('--sticky-nav-height');
      var total = header + nav;
      return total > 0 ? total : fallback;
    }
  }
  if (!customElements.get('kik-sticky-nav')) {
    customElements.define('kik-sticky-nav', KikStickyNav);
  }

  class KikAccordion extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
      this.resizeObserver = null;
      this.prefersReducedMotion = false;
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.layout = this.getAttribute('data-layout') || 'default';
      this.allowMultiple = this.hasAttribute('data-allow-multiple');
      this.openFirst = this.hasAttribute('data-open-first');

      this.setAttribute('data-layout', this.layout);

      try {
        this.prefersReducedMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      } catch(e) {
        this.prefersReducedMotion = false;
      }
      this.setupResizeObserver();

      this.initAccordionItems();
    }

    setupResizeObserver() {
      if (typeof ResizeObserver === 'undefined') return;

      this.resizeObserver = new ResizeObserver(entries => {
        entries.forEach(entry => {
          const panelInner = entry.target;
          const panel = panelInner.closest('.kik-accordion__panel, .kik-accordion-content');
          const trigger = panel?.previousElementSibling;

          if (!panel || !trigger || trigger.getAttribute('aria-expanded') !== 'true') {
            return;
          }

          panel.style.maxHeight = this.prefersReducedMotion ? 'none' : `${panelInner.offsetHeight}px`;
        });
      });
    }

    initAccordionItems() {
      const items = this.querySelectorAll('kik-accordion-item');

      items.forEach((item, index) => {
        const trigger = item.querySelector('.kik-accordion__trigger, .kik-accordion-button');
        const panel = item.querySelector('.kik-accordion__panel, .kik-accordion-content');
        const panelInner = panel?.querySelector('.kik-accordion__panel-inner, .kik-accordion-content-inner');

        if (!trigger || !panel || !panelInner) return;

        if (this.resizeObserver) {
          this.resizeObserver.observe(panelInner);
        }

        // Open first item if requested
        if (index === 0 && this.openFirst) {
          requestAnimationFrame(() => this.openAccordionItem(trigger, panel, panelInner, true));
        }

        trigger.addEventListener('click', () => {
          const isOpen = trigger.getAttribute('aria-expanded') === 'true';

          if (!this.allowMultiple) {
            this.closeAllExcept(trigger);
          }

          if (isOpen) {
            this.closeAccordionItem(trigger, panel, panelInner);
          } else {
            this.openAccordionItem(trigger, panel, panelInner, true);
          }
        });
      });

      // Fallback: if openFirst is requested but nothing opened (e.g., images/fonts not measured yet),
      // try again after a short delay.
      if (this.openFirst) {
        const anyOpen = Array.from(this.querySelectorAll('.kik-accordion__trigger')).some(t => t.getAttribute('aria-expanded') === 'true');
        if (!anyOpen && items.length) {
          const first = items[0];
          const trigger = first.querySelector('.kik-accordion__trigger, .kik-accordion-button');
          const panel = first.querySelector('.kik-accordion__panel, .kik-accordion-content');
          const panelInner = panel?.querySelector('.kik-accordion__panel-inner, .kik-accordion-content-inner');
          if (trigger && panel && panelInner) {
            setTimeout(() => this.openAccordionItem(trigger, panel, panelInner, true), 120);
          }
        }
      }
    }

    closeAllExcept(activeTrigger) {
      const triggers = this.querySelectorAll('.kik-accordion__trigger, .kik-accordion-button');

      triggers.forEach(trigger => {
        if (trigger === activeTrigger) return;
        if (trigger.getAttribute('aria-expanded') !== 'true') return;

        const panel = trigger.nextElementSibling;
        const panelInner = panel?.querySelector('.kik-accordion__panel-inner, .kik-accordion-content-inner');

        if (panel && panelInner) {
          this.closeAccordionItem(trigger, panel, panelInner);
        }
      });
    }

    openAccordionItem(trigger, panel, panelInner, animate) {
      const item = trigger.closest('kik-accordion-item');

      trigger.setAttribute('aria-expanded', 'true');
      panel.setAttribute('aria-hidden', 'false');

      if (item) {
        item.setAttribute('data-state', 'open');
      }

      if (this.prefersReducedMotion || !animate) {
        panel.style.maxHeight = 'none';
        panel.setAttribute('data-animate', 'false');
        return;
      }

      const targetHeight = panelInner.offsetHeight;
      panel.setAttribute('data-animate', 'true');
      panel.style.maxHeight = `${targetHeight}px`;
    }

    closeAccordionItem(trigger, panel, panelInner) {
      const item = trigger.closest('kik-accordion-item');

      trigger.setAttribute('aria-expanded', 'false');
      panel.setAttribute('aria-hidden', 'true');

      if (item) {
        item.setAttribute('data-state', 'closed');
      }

      if (this.prefersReducedMotion) {
        panel.style.maxHeight = '0';
        panel.setAttribute('data-animate', 'false');
        return;
      }

      const isAnimating = panel.getAttribute('data-animate') === 'true';
      const currentHeight = panelInner.offsetHeight;

      panel.style.maxHeight = `${currentHeight}px`;

      if (!isAnimating) {
        requestAnimationFrame(() => {
          panel.style.maxHeight = '0';
        });
      } else {
        panel.style.maxHeight = '0';
      }

      panel.setAttribute('data-animate', 'true');
    }
  }
  customElements.define('kik-accordion', KikAccordion);

  if (!customElements.get('kik-accordion-item')) {
    class KikAccordionItem extends HTMLElement {
      constructor() {
        super();
        this.initialized = false;
      }

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
      }
    }

    customElements.define('kik-accordion-item', KikAccordionItem);
  }

  class KikSwiperAccordion extends HTMLElement {
    connectedCallback() {
      this._bindMedia();
      this._handleMatch(this._mq ? this._mq.matches : true);
    }
    disconnectedCallback() {
      this._unbindMedia();
      this._cleanup();
    }
    _bindMedia() {
      if (this._mq) return;
      this._mq = window.matchMedia('(min-width: 1024px)');
      this._mqHandler = (ev) => this._handleMatch(ev.matches);
      if (this._mq.addEventListener) {
        this._mq.addEventListener('change', this._mqHandler);
      } else if (this._mq.addListener) {
        this._mq.addListener(this._mqHandler);
      }
    }
    _unbindMedia() {
      if (!this._mq) return;
      if (this._mq.removeEventListener) {
        this._mq.removeEventListener('change', this._mqHandler);
      } else if (this._mq.removeListener) {
        this._mq.removeListener(this._mqHandler);
      }
      this._mq = null;
      this._mqHandler = null;
    }
    _handleMatch(matches) {
      if (matches) {
        this._ensure();
      } else {
        this._cleanup();
      }
    }
    _ensure() {
      if (this._inited) return;
      window.loadSwiper().then(() => this._init());
    }
    _init() {
      const root = this;
      const sliderEl = root.querySelector('.kik-swipper3__slider');
      if (!sliderEl) return;
      const prevEl = root.querySelector('.sw3-prev');
      const nextEl = root.querySelector('.sw3-next');
      const autoplay = this.getAttribute('data-autoplay') === 'true';
      const delay = parseInt(this.getAttribute('data-delay') || '5000');
      const opts = {
        loop: true,
        speed: 500,
        effect: 'fade',
        fadeEffect: { crossFade: true },
        slidesPerView: 1,
        spaceBetween: 0,
        allowTouchMove: true,
        grabCursor: true,
        navigation: { prevEl, nextEl },
      };
      if (autoplay) {
        opts.autoplay = { delay: Math.max(1000, delay), disableOnInteraction: false, pauseOnMouseEnter: true };
      }
      this.swiper = new Swiper(sliderEl, opts);
      const items = Array.from(root.querySelectorAll('.sw3-item'));
      this._items = items;
      this._panels = items.map((el) => el.querySelector('.sw3-panel'));
      items.forEach((el) => {
        const i = parseInt(el.getAttribute('data-index') || '0', 10);
        el.addEventListener('click', () => {
          if (this.swiper && this.swiper.slideToLoop) {
            this.swiper.slideToLoop(i);
          } else if (this.swiper) {
            this.swiper.slideTo(i);
          }
        });
      });
      this._onChange = () => {
        const idx = this._real();
        this._animateTo(idx);
      };
      this.swiper.on('slideChange', this._onChange);
      this._setActive(this._real(), true);
      this._inited = true;
    }
    _real() {
      const s = this.swiper;
      return s && typeof s.realIndex === 'number' ? s.realIndex : s ? s.activeIndex : 0;
    }
    _setActive(idx, immediate = false) {
      if (!this._items) return;
      this._items.forEach((el, i) => {
        const panel = this._panels ? this._panels[i] : el.querySelector('.sw3-panel');
        const active = i === idx;
        el.classList.toggle('is-active', active);
        if (panel) {
          if (immediate) {
            panel.style.transitionProperty = 'none';
            panel.style.height = active ? 'auto' : '0px';
            panel.offsetHeight;
            panel.style.transitionProperty = '';
          } else {
            if (active) {
              this._expandPanel(panel);
            } else {
              this._collapsePanel(panel);
            }
          }
        }
      });
      this._activeIndex = idx;
    }
    _expandPanel(panel) {
      return new Promise((resolve) => {
        const inner = panel.querySelector('.sw3-panel-inner');
        const start = panel.offsetHeight;
        const target = inner ? inner.scrollHeight : panel.scrollHeight;
        if (Math.abs(target - start) < 1) {
          panel.style.height = 'auto';
          resolve();
          return;
        }
        panel.style.height = start + 'px';
        panel.offsetHeight;
        panel.style.height = target + 'px';
        const done = (e) => {
          if (e.propertyName !== 'height') return;
          panel.style.height = 'auto';
          panel.removeEventListener('transitionend', done);
          resolve();
        };
        panel.addEventListener('transitionend', done);
      });
    }
    _collapsePanel(panel) {
      return new Promise((resolve) => {
        const current =
          panel.style.height === 'auto' || panel.offsetHeight === 0 ? panel.scrollHeight || 0 : panel.offsetHeight;
        if (current <= 0) {
          panel.style.height = '0px';
          resolve();
          return;
        }
        panel.style.height = current + 'px';
        panel.offsetHeight;
        panel.style.height = '0px';
        const done = (e) => {
          if (e.propertyName !== 'height') return;
          panel.removeEventListener('transitionend', done);
          resolve();
        };
        panel.addEventListener('transitionend', done);
      });
    }
    _animateTo(targetIdx) {
      if (!this._items) return;
      let prevIdx =
        typeof this._activeIndex === 'number'
          ? this._activeIndex
          : this._items.findIndex((el) => el.classList.contains('is-active'));
      if (prevIdx === -1) prevIdx = this._real();
      if (prevIdx === targetIdx) return;
      if (this._animating) {
        this._requestedIndex = targetIdx;
        return;
      }
      this._animating = true;
      this._requestedIndex = null;
      const prevItem = this._items[prevIdx];
      const nextItem = this._items[targetIdx];
      const prevPanel = prevItem
        ? this._panels
          ? this._panels[prevIdx]
          : prevItem.querySelector('.sw3-panel')
        : null;
      const nextPanel = nextItem
        ? this._panels
          ? this._panels[targetIdx]
          : nextItem.querySelector('.sw3-panel')
        : null;
      if (prevItem) prevItem.classList.remove('is-active');
      if (nextItem) nextItem.classList.add('is-active');
      const tasks = [];
      if (prevPanel) tasks.push(this._collapsePanel(prevPanel));
      if (nextPanel) tasks.push(this._expandPanel(nextPanel));
      const wait = tasks.length ? Promise.all(tasks) : Promise.resolve();
      wait
        .then(() => {
          this._activeIndex = targetIdx;
        })
        .finally(() => {
          this._animating = false;
          if (typeof this._requestedIndex === 'number' && this._requestedIndex !== this._activeIndex) {
            const nextIdx = this._requestedIndex;
            this._requestedIndex = null;
            this._animateTo(nextIdx);
          } else {
            this._requestedIndex = null;
          }
        });
    }
    _cleanup() {
      if (this.swiper) {
        if (this._onChange) this.swiper.off('slideChange', this._onChange);
        try {
          this.swiper.destroy(true, true);
        } catch (e) {}
        this.swiper = null;
      }
      this._items = null;
      this._panels = null;
      this._inited = false;
      this._animating = false;
      this._requestedIndex = null;
    }
  }
  if (!customElements.get('kik-swiper-accordion')) {
    customElements.define('kik-swiper-accordion', KikSwiperAccordion);
  }
  class KikYoutube extends HTMLElement {
    connectedCallback() {
      this._inited || this._ensure();
    }
    disconnectedCallback() {
      this._cleanup();
    }
    _ensure() {
      if (this._inited) return;
      window.loadSwiper().then(() => this._init());
    }
    _init() {
      const root = this;
      const sliderEl = root.querySelector('.kik-swiper__slider');
      if (!sliderEl) return;
      const prevEl = root.querySelector('.kik-swiper__prev');
      const nextEl = root.querySelector('.kik-swiper__next');
      const modal = root.querySelector('.kik-yt__modal');
      const frame = root.querySelector('.kik-yt__frame');
      const closeBtn = root.querySelector('.kik-yt__close');
      this.swiper = new Swiper(sliderEl, {
        loop: false,
        speed: 500,
        slidesPerView: 1.1,
        slidesPerGroup: 1,
        spaceBetween: 20,
        allowTouchMove: true,
        grabCursor: true,
        watchOverflow: true,
        navigation: { prevEl, nextEl },
        breakpoints: {
          480: { slidesPerView: 1.1, slidesPerGroup: 1.1, spaceBetween: 20, },
          640: { slidesPerView: 2.2, slidesPerGroup: 2.2, spaceBetween: 20, },
          1024: { slidesPerView: 3.2, slidesPerGroup: 3.2, spaceBetween: 20, },
        },
      });
      const updateNav = () => {
        const b = this.swiper.isBeginning;
        const e = this.swiper.isEnd;
        if (prevEl) {
          prevEl.disabled = b;
          prevEl.classList.toggle('opacity-40', b);
          prevEl.classList.toggle('cursor-not-allowed', b);
          prevEl.classList.toggle('pointer-events-none', b);
        }
        if (nextEl) {
          nextEl.disabled = e;
          nextEl.classList.toggle('opacity-40', e);
          nextEl.classList.toggle('cursor-not-allowed', e);
          nextEl.classList.toggle('pointer-events-none', e);
        }
      };
      updateNav();
      this.swiper.on('slideChange', updateNav);
      this.swiper.on('resize', updateNav);

      const open = (id) => {
        if (!modal || !frame) return;
        modal.classList.remove('hidden');
        frame.innerHTML =
          '<iframe class="w-full h-full" src="https://www.youtube.com/embed/' +
          id +
          '?autoplay=1&rel=0&playsinline=1" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>';
      };
      const close = () => {
        if (!modal || !frame) return;
        modal.classList.add('hidden');
        frame.innerHTML = '';
      };
      // Save refs for cleanup and bind removable listeners
      this._modalEl = modal;
      this._frameEl = frame;
      this._closeBtnEl = closeBtn;
      this._closeHandler = close;
      if (closeBtn) closeBtn.addEventListener('click', this._closeHandler);
      if (modal) {
        this._modalClickHandler = (e) => {
          if (e.target === modal || e.target.classList.contains('kik-yt__modal')) close();
        };
        modal.addEventListener('click', this._modalClickHandler);
      }
      this._escHandler = (e) => { if (e.key === 'Escape') close(); };
      document.addEventListener('keydown', this._escHandler);
      root.querySelectorAll('.kik-yt__card').forEach((btn) => {
        btn.addEventListener('click', () => {
          const id = btn.getAttribute('data-ytid');
          if (id) open(id);
        });
      });
      this._autoFillMeta(root);
      this._inited = true;
    }
    _parseVideoId(raw) {
      if (!raw) return '';
      var value = String(raw).trim();
      if (!value) return '';
      if (/^[a-zA-Z0-9_-]{11}$/.test(value)) return value;
      try {
        var url = new URL(value);
        var v = url.searchParams.get('v');
        if (v) return v;
        if (url.hostname === 'youtu.be') {
          var p = url.pathname.replace(/^\/+/, '');
          if (p) return p;
        }
      } catch (e) {}
      return value;
    }
    _autoFillMeta(root) {
      var apiKey = (root.getAttribute('data-api-key') || '').trim();
      var cards = root.querySelectorAll('.kik-yt__card');
      if (!cards.length || typeof fetch !== 'function') return;
      if (!apiKey) {
        console.warn('No API key provided for YouTube auto-fill');
        return;
      }
      var videoCache = {};
      var channelCache = {};
      var getVideoMeta = function(videoId) {
        if (videoCache[videoId]) return Promise.resolve(videoCache[videoId]);
        var url =
          'https://www.googleapis.com/youtube/v3/videos?part=snippet&id=' +
          encodeURIComponent(videoId) +
          '&key=' +
          encodeURIComponent(apiKey);
        return fetch(url)
          .then(function(res) { return res.ok ? res.json() : null; })
          .then(function(data) {
            if (!data || !data.items || !data.items.length) return null;
            var snippet = data.items[0].snippet || {};
            var thumbs = snippet.thumbnails || {};
            var thumbUrl =
              (thumbs.maxres && thumbs.maxres.url) ||
              (thumbs.standard && thumbs.standard.url) ||
              (thumbs.high && thumbs.high.url) ||
              (thumbs.medium && thumbs.medium.url) ||
              (thumbs.default && thumbs.default.url) ||
              '';
            var meta = {
              title: snippet.title || '',
              channelTitle: snippet.channelTitle || '',
              channelId: snippet.channelId || '',
              thumbUrl: thumbUrl
            };
            videoCache[videoId] = meta;
            return meta;
          });
      };
      var getChannelMeta = function(channelId) {
        if (!channelId) return Promise.resolve(null);
        if (channelCache[channelId]) return Promise.resolve(channelCache[channelId]);
        var url =
          'https://www.googleapis.com/youtube/v3/channels?part=snippet&id=' +
          encodeURIComponent(channelId) +
          '&key=' +
          encodeURIComponent(apiKey);
        return fetch(url)
          .then(function(res) { return res.ok ? res.json() : null; })
          .then(function(data) {
            if (!data || !data.items || !data.items.length) return null;
            var snippet = data.items[0].snippet || {};
            var thumbs = snippet.thumbnails || {};
            var avatarUrl =
              (thumbs.high && thumbs.high.url) ||
              (thumbs.medium && thumbs.medium.url) ||
              (thumbs.default && thumbs.default.url) ||
              '';
            var meta = { avatarUrl: avatarUrl };
            channelCache[channelId] = meta;
            return meta;
          });
      };
      cards.forEach((card) => {
        var rawId = card.getAttribute('data-ytid') || '';
        var videoId = this._parseVideoId(rawId);
        if (!videoId) return;
        getVideoMeta(videoId)
          .then(function(videoMeta) {
            if (!videoMeta) return null;
            var titleEl = card.querySelector('.kik-yt__title');
            var channelEl = card.querySelector('.kik-yt__channel');
            var imgEl = card.querySelector('.kik-yt__thumb img[data-default-thumb]');
            if (titleEl && videoMeta.title) {
              titleEl.textContent = videoMeta.title;
            }
            if (channelEl && videoMeta.channelTitle) {
              channelEl.textContent = videoMeta.channelTitle;
            }
            if (imgEl && videoMeta.thumbUrl) {
              imgEl.src = videoMeta.thumbUrl;
              imgEl.removeAttribute('data-default-thumb');
            }
            return getChannelMeta(videoMeta.channelId);
          })
          .then(function(channelMeta) {
            if (!channelMeta) return;
            var avatarEl = card.querySelector('.kik-yt__avatar');
            if (!avatarEl || avatarEl.querySelector('img') || !channelMeta.avatarUrl) return;
            var img = document.createElement('img');
            img.src = channelMeta.avatarUrl;
            img.alt = (card.getAttribute('data-channel') || '') + ' avatar';
            img.loading = 'lazy';
            avatarEl.innerHTML = '';
            avatarEl.appendChild(img);
          })
          .catch(function() {});
      });
    }
    _cleanup() {
      // Remove bound listeners for this instance
      if (this._closeBtnEl && this._closeHandler) {
        try { this._closeBtnEl.removeEventListener('click', this._closeHandler); } catch(e) {}
      }
      if (this._modalEl && this._modalClickHandler) {
        try { this._modalEl.removeEventListener('click', this._modalClickHandler); } catch(e) {}
      }
      if (this._escHandler) {
        try { document.removeEventListener('keydown', this._escHandler); } catch(e) {}
      }
      this._closeBtnEl = null;
      this._modalEl = null;
      this._frameEl = null;
      this._closeHandler = null;
      this._modalClickHandler = null;
      this._escHandler = null;

      if (this.swiper) {
        try {
          this.swiper.destroy(true, true);
        } catch (e) {}
        this.swiper = null;
      }
      this._inited = false;
    }
  }
  if (!customElements.get('kik-youtube')) customElements.define('kik-youtube', KikYoutube);

  class KikSwiper extends HTMLElement {
    connectedCallback() {
      this._inited || this._ensure();
    }
    disconnectedCallback() {
      this._cleanup();
    }
    _ensure() {
      if (this._inited) return;
      window.loadSwiper().then(() => this._init());
    }
    _init() {
      const root = this;
      const sliderEl = root.querySelector('.kik-swiper__slider');
      if (!sliderEl) return;
      const prevEl = root.querySelector('.kik-swiper__prev');
      const nextEl = root.querySelector('.kik-swiper__next');

      this.swiper = new Swiper(sliderEl, {
        loop: false,
        speed: 500,
        slidesPerView: 1.1,
        slidesPerGroup: 1,
        spaceBetween: 20,
        allowTouchMove: true,
        grabCursor: true,
        watchOverflow: true,
        navigation: { prevEl, nextEl },
        breakpoints: {
          480: { slidesPerView: 1.5, slidesPerGroup: 1.5, spaceBetween: 20 },
          640: { slidesPerView: 2, slidesPerGroup: 2, spaceBetween: 20 },
          1024: { slidesPerView: 3, slidesPerGroup: 3, spaceBetween: 28 },
        },
      });

      const updateNav = () => {
        const isBeginning = this.swiper.isBeginning;
        const isEnd = this.swiper.isEnd;
        if (prevEl) {
          prevEl.disabled = isBeginning;
          prevEl.classList.toggle('opacity-40', isBeginning);
          prevEl.classList.toggle('cursor-not-allowed', isBeginning);
          prevEl.classList.toggle('pointer-events-none', isBeginning);
        }
        if (nextEl) {
          nextEl.disabled = isEnd;
          nextEl.classList.toggle('opacity-40', isEnd);
          nextEl.classList.toggle('cursor-not-allowed', isEnd);
          nextEl.classList.toggle('pointer-events-none', isEnd);
        }
      };

      updateNav();
      this.swiper.on('slideChange', updateNav);
      this.swiper.on('resize', updateNav);

      this._inited = true;
    }
    _cleanup() {
      if (this.swiper) {
        try {
          this.swiper.destroy(true, true);
        } catch (e) {}
        this.swiper = null;
      }
      this._inited = false;
    }
  }
  if (!customElements.get('kik-swiper')) customElements.define('kik-swiper', KikSwiper);

  class KikSwiperBrand extends HTMLElement{
    connectedCallback(){ this._inited || this._ensure(); }
    disconnectedCallback(){ this._cleanup(); }
    _ensure(){ if(this._inited) return; window.loadSwiper().then(()=>this._init()); }
    _init(){
      const root=this;
      const sliderEl=root.querySelector('.kik-swiper-brand__slider');
      if(!sliderEl) return;
      const prevEl=root.querySelector('.kik-swiper-brand__prev');
      const nextEl=root.querySelector('.kik-swiper-brand__next');
      const mCols=parseInt(root.dataset.mobileCols||'1');
      const dCols=parseInt(root.dataset.desktopCols||'4');
      const showArrows = (root.dataset.showArrows === 'true');
      const opts={
        loop:false,
        speed:500,
        slidesPerView: 1.1,
        slidesPerGroup: Math.max(1, mCols),
        spaceBetween: 16,
        allowTouchMove:true,
        grabCursor:true,
        watchOverflow:true,
        breakpoints:{
          480: { slidesPerView: 1.5, slidesPerGroup: 1.5, spaceBetween: 20 },
          1024:{ slidesPerView: Math.max(1, dCols), slidesPerGroup: Math.max(1, dCols), spaceBetween: 20 }
        }
      };
      if (showArrows && prevEl && nextEl) {
        opts.navigation = { prevEl, nextEl };
      }
      this.swiper = new Swiper(sliderEl, opts);

      // Equalize brand card heights across all slides
      const equalizeHeights = () => {
        const cards = sliderEl.querySelectorAll('.kik-brand-card');
        if (!cards.length) return;
        cards.forEach(c => { c.style.minHeight = ''; });
        let max = 0;
        cards.forEach(c => { const h = c.getBoundingClientRect().height; if (h > max) max = h; });
        if (max > 0) cards.forEach(c => { c.style.minHeight = max + 'px'; });
      };
      equalizeHeights();
      setTimeout(equalizeHeights, 0);
      this.swiper.on('slideChange', equalizeHeights);
      this.swiper.on('resize', equalizeHeights);
      this.swiper.on('imagesReady', equalizeHeights);
      sliderEl.querySelectorAll('img').forEach(img => { if (!img.complete) img.addEventListener('load', equalizeHeights, { once: true }); });
      const updateNav = ()=>{
        if (!showArrows) return;
        const b = this.swiper.isBeginning;
        const e = this.swiper.isEnd;
        if(prevEl){ prevEl.disabled = b; prevEl.classList.toggle('opacity-40', b); prevEl.classList.toggle('cursor-not-allowed', b); prevEl.classList.toggle('pointer-events-none', b); }
        if(nextEl){ nextEl.disabled = e; nextEl.classList.toggle('opacity-40', e); nextEl.classList.toggle('cursor-not-allowed', e); nextEl.classList.toggle('pointer-events-none', e); }
      };
      updateNav();
      this.swiper.on('slideChange', updateNav);
      this.swiper.on('resize', updateNav);
      this._inited=true;
    }
    _cleanup(){ if(this.swiper){ try{ this.swiper.destroy(true,true);}catch(e){} this.swiper=null; } this._inited=false; }
  }
  if(!customElements.get('kik-swiper-brand')) customElements.define('kik-swiper-brand', KikSwiperBrand);

  class KikSwiperBanner extends HTMLElement{
    connectedCallback(){ this._inited || this._ensure(); }
    disconnectedCallback(){ this._cleanup(); }
    _ensure(){ if(this._inited) return; window.loadSwiper().then(()=>this._init()); }
    _init(){
      const root=this;
      const sliderEl=root.querySelector('.kik-swiper-banner__slider');
      if(!sliderEl) return;
      const prevEl=root.querySelector('.kik-swiper-banner__prev');
      const nextEl=root.querySelector('.kik-swiper-banner__next');
      const paginationEl=root.querySelector('.kik-swiper-banner__pagination');
      const autoplay = (root.dataset.autoplay === 'true');
      const delay = parseInt(root.dataset.delay || '5000');
      const opts={
        loop: true,
        speed: 700,
        effect: 'slide',
        slidesPerView: 1,
        slidesPerGroup: 1,
        spaceBetween: 0,
        allowTouchMove: true,
        grabCursor: true,
        watchOverflow: true,
        autoHeight: true,
        navigation: { prevEl, nextEl },
        pagination: { el: paginationEl, clickable: true }
      };
      this.swiper = new Swiper(sliderEl, opts);
      this._setupAutoBehavior(sliderEl);
      this._inited = true;
    }
    _setupAutoBehavior(sliderEl){
      const swiper = this.swiper;
      if (!swiper) return;
      const root = this;
      const isAutoPlayVideo = (video) => {
        if (!video) return false;
        if (video.hasAttribute('autoplay')) return true;
        const d = video.getAttribute('data-autoplay');
        return d === 'true' || d === '';
      };
      const handle = () => {
        if (this._autoTimer) {
          clearTimeout(this._autoTimer);
          this._autoTimer = null;
        }
        if (this._videoPollTimer) {
          clearInterval(this._videoPollTimer);
          this._videoPollTimer = null;
        }
        const allVideos = sliderEl.querySelectorAll('video');
        allVideos.forEach((v) => {
          try { v.pause(); } catch(e) {}
          if (v._bannerOnEnded) {
            try { v.removeEventListener('ended', v._bannerOnEnded); } catch(e) {}
            v._bannerOnEnded = null;
          }
        });
        if (!this.swiper) return;
        const s = this.swiper;
        const activeSlide = s.slides[s.activeIndex];
        if (!activeSlide) return;
        const autoplayEnabled = (root.getAttribute('data-autoplay') === 'true');
        const delayAttr = root.getAttribute('data-delay');
        const autoplayDelay = parseInt(delayAttr || '5000', 10);
        const localWrapper = activeSlide.querySelector('video-media:not([host])');
        let activeVideo = null;
        const vids = activeSlide.querySelectorAll('video');
        if (vids && vids.length) {
          Array.prototype.some.call(vids, function(v){
            const st = window.getComputedStyle(v);
            if (st && st.display !== 'none' && st.visibility !== 'hidden' && v.offsetWidth + v.offsetHeight > 0) {
              activeVideo = v; return true;
            }
            return false;
          });
          if (!activeVideo) { activeVideo = vids[0]; }
        }
        if (!autoplayEnabled) {
          // Auto slide disabled: just play active video (if any), never auto-advance, keep native loop behaviour.
          if (activeVideo && isAutoPlayVideo(activeVideo)) {
            activeVideo.muted = true;
            const p0 = activeVideo.play && activeVideo.play();
            if (p0 && typeof p0.catch === 'function') { p0.catch(function(){}); }
          } else if (!activeVideo && localWrapper) {
            // Video tag not in DOM yet (deferred-media). Poll until it appears, then just play once (loop left intact).
            let tries = 0;
            const pollNoAdvance = () => {
              if (tries++ > 20) {
                clearInterval(this._videoPollTimer);
                this._videoPollTimer = null;
                return;
              }
              const v = localWrapper.querySelector('video');
              if (!v) return;
              clearInterval(this._videoPollTimer);
              this._videoPollTimer = null;
              v.muted = true;
              const p1 = v.play && v.play();
              if (p1 && typeof p1.catch === 'function') { p1.catch(function(){}); }
            };
            this._videoPollTimer = setInterval(pollNoAdvance, 250);
          }
          return;
        }
        // Auto slide enabled
        if (activeVideo && isAutoPlayVideo(activeVideo)) {
          // Video slide: wait for video end to advance.
          activeVideo.muted = true;
          activeVideo.loop = false;
          if (activeVideo.hasAttribute('loop')) activeVideo.removeAttribute('loop');
          const onEnded = () => {
            if (!this.swiper) return;
            this.swiper.slideNext();
          };
          activeVideo._bannerOnEnded = onEnded;
          activeVideo.addEventListener('ended', onEnded, { once: true });
          const p = activeVideo.play && activeVideo.play();
          if (p && typeof p.catch === 'function') { p.catch(function(){}); }
        } else if (!activeVideo && localWrapper) {
          // Local video via <video-media> but <video> not yet in DOM (first time). Poll until it appears,
          // then behave like the branch above: play video and advance on ended. Do NOT set a time-based auto slide here.
          let tries2 = 0;
          const pollWithAdvance = () => {
            if (tries2++ > 20) {
              clearInterval(this._videoPollTimer);
              this._videoPollTimer = null;
              return;
            }
            const v = localWrapper.querySelector('video');
            if (!v) return;
            clearInterval(this._videoPollTimer);
            this._videoPollTimer = null;
            v.muted = true;
            v.loop = false;
            if (v.hasAttribute('loop')) v.removeAttribute('loop');
            const onEnded2 = () => {
              if (!this.swiper) return;
              this.swiper.slideNext();
            };
            v._bannerOnEnded = onEnded2;
            v.addEventListener('ended', onEnded2, { once: true });
            const p2 = v.play && v.play();
            if (p2 && typeof p2.catch === 'function') { p2.catch(function(){}); }
          };
          this._videoPollTimer = setInterval(pollWithAdvance, 250);
        } else if (autoplayDelay && autoplayDelay > 0) {
          // Image (or non-video) slide: auto-advance after delay.
          this._autoTimer = setTimeout(() => {
            if (!this.swiper) return;
            this.swiper.slideNext();
          }, Math.max(1000, autoplayDelay));
        }
      };
      this._bannerSlideHandler = handle;
      handle();
      swiper.on('slideChangeTransitionStart', handle);
    }
    _cleanup(){
      if (this._autoTimer) {
        clearTimeout(this._autoTimer);
        this._autoTimer = null;
      }
      if (this._videoPollTimer) {
        clearInterval(this._videoPollTimer);
        this._videoPollTimer = null;
      }
      if (this.swiper) {
        if (this._bannerSlideHandler && typeof this.swiper.off === 'function') {
          try { this.swiper.off('slideChangeTransitionStart', this._bannerSlideHandler); } catch(e) {}
        }
        try{ this.swiper.destroy(true,true);}catch(e){}
        this.swiper=null;
      }
      this._bannerSlideHandler = null;
      this._inited=false;
    }
  }
  if(!customElements.get('kik-swiper-banner')) customElements.define('kik-swiper-banner', KikSwiperBanner);
})();
